import { __decorate, __metadata } from 'tslib';
import { EntityStore, StoreConfig, QueryEntity, QueryConfig, Order, isObject, isString, isDefined, guid, EntityCollectionPlugin, isUndefined, isFunction, compareValues } from '@datorama/akita';
import { BehaviorSubject, of, combineLatest, isObservable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

let AkitaFiltersStore = class AkitaFiltersStore extends EntityStore {
    constructor(storeName) {
        super(undefined, { name: storeName });
    }
};
AkitaFiltersStore = __decorate([
    StoreConfig({ name: 'filters' }),
    __metadata("design:paramtypes", [String])
], AkitaFiltersStore);

let AkitaFiltersQuery = class AkitaFiltersQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
};
AkitaFiltersQuery = __decorate([
    QueryConfig({
        sortBy: 'order',
        sortByOrder: Order.ASC
    }),
    __metadata("design:paramtypes", [AkitaFiltersStore])
], AkitaFiltersQuery);

/**
 * Helper function to do a default filter
 */
function defaultFilter(value, index, array, filter) {
    if (isObject(value) && isString(filter.value)) {
        return searchFilter(filter.value, value);
    }
    return isDefined(filter.value) ? filter.value === value : !!value;
}
/**
 * Helper function to do search on all string element
 */
function searchFilter(searchKey, inObj) {
    return isString(searchKey) && Object.keys(inObj).some(function (key) {
        return isString(inObj[key]) && inObj[key].toLocaleLowerCase().includes(searchKey.toLocaleLowerCase());
    });
}
/**
 * Helper function to do search in one key of an object
 */
function searchFilterIn(searchKey, inObj, inKey) {
    return isString(searchKey)
        && isString(inKey)
        && isString(inObj[inKey])
        && inObj[inKey].toLocaleLowerCase().includes(searchKey.toLocaleLowerCase());
}
/**
 * Function to compare changes between two AkitaFilters arrays
 */
function compareFiltersArray(x, y) {
    if (!x && !y) {
        return true;
    }
    if (!x || !y || x.length !== y.length) {
        return false;
    }
    return !x.some((filterX) => {
        const find = y.find(filterY => filterY.id === filterX.id);
        return !(find && find.value === filterX.value);
    });
}
const getCompareFilters = () => {
    return compareFiltersArray;
};

function capitalize(str) {
    return str.charAt(0).toUpperCase() + str.substr(1);
}
;
function createFilter(filterParams) {
    var _a;
    const id = filterParams.id ? filterParams.id : guid();
    const name = filterParams.name || (filterParams.value && filterParams.id ?
        `${capitalize(filterParams.id.toString())}: ${filterParams.value.toString()}` : undefined);
    if (!((_a = filterParams) === null || _a === void 0 ? void 0 : _a.predicate) && filterParams.value && !filterParams.server) {
        /** use default function, if not provided */
        // @ts-ignore
        filterParams.predicate = defaultFilter;
    }
    return Object.assign({ id, name, hide: false, order: 10, server: false }, filterParams);
}

class AkitaFiltersPlugin extends EntityCollectionPlugin {
    constructor(query, params = {}) {
        super(query, params.entityIds);
        this.query = query;
        this.params = params;
        this._refresh$ = new BehaviorSubject(0);
        this._server = false;
        this._withServerOptions = {};
        this.params = Object.assign({ filtersStoreName: this.getStore().storeName + 'Filters' }, params);
        this._filtersStore = (params.filtersStore) ? params.filtersStore : new AkitaFiltersStore(this.params.filtersStoreName);
        this._filtersQuery = (params.filtersQuery) ? params.filtersQuery : new AkitaFiltersQuery(this._filtersStore);
        this._selectFilters$ = this.filtersQuery.selectAll({ sortBy: 'order' });
        this._selectFiltersAll$ = this.filtersQuery.selectAll({ sortBy: 'order', filterBy: filter => !(filter === null || filter === void 0 ? void 0 : filter.hide) });
        this._selectSortBy$ = this.filtersQuery.select(state => state === null || state === void 0 ? void 0 : state.sort);
    }
    get withServerOptions() {
        return this._withServerOptions;
    }
    set withServerOptions(value) {
        this._withServerOptions = value;
    }
    get filtersStore() {
        return this._filtersStore;
    }
    get filtersQuery() {
        return this._filtersQuery;
    }
    /**
     *  Add support of filters from server. Provide a function that will be call each time a filter changes
     *
     *  new AkitaFilterPlugins(query).withServer((filters) => {
     *      return this.api.getData(filters);
     *  });
     *
     *  Return false to not add in store. if you want to manage the store in your own.
     */
    withServer(onChangeFilter, options = {}) {
        var _a;
        options = Object.assign({}, options);
        this.server = true;
        this.withServerOptions = options;
        this._onChangeFilter = onChangeFilter;
        // Change default select filters to remove server filters, if you use selectAllByFilters();
        this._selectFilters$ = this._filtersQuery.selectAll({ sortBy: 'order', filterBy: filter => { var _a; return !((_a = filter) === null || _a === void 0 ? void 0 : _a.server); } });
        const listObservable = [];
        listObservable.push(this._filtersQuery.selectAll({ sortBy: 'order', filterBy: filter => { var _a; return ((_a = filter) === null || _a === void 0 ? void 0 : _a.server) === true; } })
            .pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))));
        if (this.withServerOptions.withSort) {
            listObservable.push((_a = this.selectSortBy()) !== null && _a !== void 0 ? _a : of(null));
        }
        listObservable.push(this._refresh$);
        combineLatest(listObservable)
            .pipe(map((data) => {
            return this.getNormalizedFilters(this.withServerOptions);
        })).subscribe((normalizerFilters) => {
            const returnOnChange = this._onChangeFilter(normalizerFilters);
            if (returnOnChange !== false && isObservable(returnOnChange)) {
                if (this._lastServerSubscription) {
                    this._lastServerSubscription.unsubscribe();
                }
                this._lastServerSubscription = returnOnChange.subscribe((newValue) => {
                    this.getStore().set(newValue);
                });
            }
        });
        return this;
    }
    get server() {
        return this._server;
    }
    set server(value) {
        this._server = value;
    }
    /** Return true, if server is configured **/
    hasServer() {
        return this.server;
    }
    /**
     *  Select all filters
     *
     *  Note: Only all filters not hide (with hide=true), will not be displayed. If you want it, call directly to:
     * `this.filtersQuery.selectAll()`
     *
     *
     */
    selectFilters() {
        return this._selectFiltersAll$;
    }
    /**
     * Get all the current snapshot filters
     *
     *  Note: filters with hide=true, will not be displayed. If you want it, call directly to:
     * `this.filtersQuery.getAll()`
     */
    getFilters() {
        return this._filtersQuery.getAll({ filterBy: filter => !filter.hide });
    }
    /**
     * Get all the current snapshot server filters (only if server is available else return default not hidden filters)
     *
     *  Note: filters with server=false, will not be displayed. If you want it, call directly to:
     * `this.filtersQuery.getAll()`
     */
    getServerFilters() {
        return this.server ?
            this._filtersQuery.getAll({ filterBy: filter => { var _a; return (_a = filter) === null || _a === void 0 ? void 0 : _a.server; } })
            : this.getFilters();
    }
    /**
     * Select All Entity with apply filter to it, and updated with any change (entity or filter)
     * Will not apply sort, if need return   asObject:true !
     */
    selectAllByFilters(options) {
        var _a;
        const listObservable = [];
        listObservable.push(this._selectFilters$, this.getQuery().selectAll(options), this._refresh$);
        if (options && options.asObject) {
            return combineLatest(listObservable).pipe(map((values) => {
                const [filters, entities, refresh] = values;
                const unkNowEntity = entities;
                return this._applyFiltersForHashMap(unkNowEntity, filters);
            }));
        }
        else {
            let sortBy = this.selectSortBy();
            if (this.server && ((_a = this._withServerOptions) === null || _a === void 0 ? void 0 : _a.withSort)) {
                sortBy = sortBy.pipe(map(data => null));
            }
            listObservable.push(sortBy);
            return combineLatest(listObservable).pipe(map((values) => {
                const [filters, entities, refresh, sort] = values;
                const unkNowEntity = entities;
                return this._applyFiltersForArray(unkNowEntity, filters, sort);
            }));
        }
    }
    /**
     * Get All Entity with apply filter to it, and updated with any change (entity or filter)
     * Will not apply sort, if need return   asObject:true !
     */
    getAllByFilters(options) {
        const filters = this.filtersQuery.getAll();
        const unkNowEntity = this.getQuery().getAll(options);
        if (options && options.asObject) {
            return this._applyFiltersForHashMap(unkNowEntity, filters);
        }
        else {
            const sort = this.getSortBy();
            return this._applyFiltersForArray(unkNowEntity, filters, sort);
        }
    }
    getSortBy() {
        const state = this.filtersQuery.getValue();
        return state && state.sort ? state.sort : null;
    }
    /**
     * Create or update a filter
     */
    setFilter(filter) {
        filter = this.updateServerIfNeeded(filter);
        const entity = createFilter(filter);
        // noinspection TypeScriptValidateTypes
        this.filtersStore.upsert(entity.id, entity);
    }
    updateServerIfNeeded(filter) {
        var _a;
        if (this.server && isUndefined((_a = filter) === null || _a === void 0 ? void 0 : _a.server)) {
            filter.server = true;
        }
        return filter;
    }
    /**
     * Create or update multiples filters
     */
    setFilters(filters) {
        if (!filters) {
            return;
        }
        const entities = filters.map((filter => {
            filter = this.updateServerIfNeeded(filter);
            return createFilter(filter);
        }));
        this.filtersStore.upsertMany(entities);
    }
    /**
     * Remove a Filter
     */
    removeFilter(id) {
        this.filtersStore.remove(id);
    }
    /**
     * Remove a Filter
     */
    removeFilters(ids) {
        this.filtersStore.remove(ids);
    }
    /**
     * Clear all filters
     */
    clearFilters() {
        this.filtersStore.remove();
    }
    /**
     * Get filter value, return null, if value not available
     */
    getFilterValue(id) {
        if (this.filtersQuery.hasEntity(id)) {
            const entity = this.filtersQuery.getEntity(id);
            return entity.value ? entity.value : null;
        }
        return null;
    }
    /**
     * Get filter value, return null, if value not available
     */
    getSortValue() {
        const state = this.filtersQuery.getValue();
        return state.sort ? state.sort : null;
    }
    /**
     * Select Sort by value
     */
    selectSortBy() {
        return this._selectSortBy$;
    }
    /**
     * Set orderBy
     */
    setSortBy(order) {
        this.filtersStore.update({ sort: order });
    }
    /**
     * Get the filters normalized as key value or as query params.
     * This can be useful for server-side filtering
     */
    getNormalizedFilters(options = {}) {
        const result = {};
        options = Object.assign({ sortByKey: 'sortBy', sortByOrderKey: 'sortByOrder' }, options);
        for (const filter of this.getServerFilters()) {
            result[filter.id] = filter.value;
        }
        const sort = this.getSortValue();
        if (options.withSort && (sort === null || sort === void 0 ? void 0 : sort.sortBy)) {
            result[options.sortByKey] = sort === null || sort === void 0 ? void 0 : sort.sortBy;
            result[options.sortByOrderKey] = sort === null || sort === void 0 ? void 0 : sort.sortByOrder;
        }
        if (options.asQueryParams) {
            return this._serialize(result);
        }
        return result;
    }
    destroy() {
        this.clearFilters();
    }
    /** This method is responsible for getting access to the query. */
    getQuery() {
        return this.query;
    }
    /** This method is responsible for getting access to the store. */
    getStore() {
        return this.getQuery().__store__;
    }
    /**
     * Trigger a refresh of the data. This will force the library to replay all filters.
     * Very useful for the withServer feature, because it allows to call back the with server callback function with the same parameters, to make a new call to the api.
     */
    refresh() {
        const value = this._refresh$.getValue() + 1;
        this._refresh$.next(value);
        return value;
    }
    _serialize(obj) {
        return Object.keys(obj)
            .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(obj[k])}`)
            .join('&');
    }
    _applyFiltersForArray(entities, filters, sort) {
        let entitiesFiltered = entities;
        if (filters.length !== 0) {
            entitiesFiltered = entities.filter((entity, index, array) => {
                return filters.every((filter) => {
                    var _a;
                    if ((_a = filter) === null || _a === void 0 ? void 0 : _a.predicate) {
                        return !!filter.predicate(entity, index, array, filter);
                    }
                    return true;
                });
            });
        }
        const sortOptions = sort;
        if (sortOptions === null || sortOptions === void 0 ? void 0 : sortOptions.sortBy) {
            const _sortBy = isFunction(sortOptions.sortBy) ? sortOptions.sortBy : compareValues(sortOptions.sortBy, sortOptions.sortByOrder);
            entitiesFiltered = [...entitiesFiltered.sort((a, b) => _sortBy(a, b, entities))];
        }
        return entitiesFiltered;
    }
    _applyFiltersForHashMap(entities, filters) {
        if (filters.length === 0) {
            return entities;
        }
        const hashMapFiltered = {};
        Object.keys(entities).forEach((entityKey, index) => {
            const entity = entities[entityKey];
            if (this._applyFiltersForOneEntity(filters, entity, index, entities)) {
                hashMapFiltered[entityKey] = entity;
            }
        });
        return hashMapFiltered;
    }
    _applyFiltersForOneEntity(filters, entity, index, array) {
        return filters.every((filter) => {
            var _a;
            if ((_a = filter) === null || _a === void 0 ? void 0 : _a.predicate) {
                return !!filter.predicate(entity, index, array, filter);
            }
            return true;
        });
    }
    instantiatePlugin(id) {
        return null;
    }
}

/*
 * Public API Surface of akita-filters-plugin
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AkitaFiltersPlugin, AkitaFiltersQuery, AkitaFiltersStore, compareFiltersArray, createFilter, defaultFilter, getCompareFilters, searchFilter, searchFilterIn };

